/**
 * ENG-9977 — the canonical roster display name.
 *
 * A synced (Clever/ClassLink) roster keeps the provider's `name` as its
 * system-of-record value, and a school renaming it in LiveSchool stores that
 * rename as `nickname`. The nickname is what the school actually calls the
 * roster, so it is what every surface must show AND search.
 *
 * Reading `name` directly is not a cosmetic slip. Provider roster names are
 * usually machine junk, which is exactly why a school renames them — so a
 * search box filtering on `name` makes a renamed roster findable only by a
 * string the teacher deliberately replaced and has no reason to remember.
 *
 * This mirrors `getStudentFirstName` (ENG-9258) for students: one resolver, so
 * display and search cannot disagree about what a thing is called. It exists
 * because there were already three hand-rolled copies of `nickname || name`
 * (`utils/accessSheetLabels.ts`, `routes/setup.rosters.index.lazy.tsx`,
 * `components/setup/AddToRosterPanel.tsx`) and three surfaces that forgot it
 * entirely.
 */

/** The roster fields this resolver reads, in every casing the app produces. */
export interface RosterNameFields {
  nickname?: string | null;
  name?: string | null;
  /** Some API shapes carry the roster's name under this key instead. */
  roster_name?: string | null;
}

/**
 * Resolve a roster's display name: `nickname || name || roster_name`.
 *
 * A whitespace-only nickname is treated as absent — the rename dialog permits
 * it, and falling through to the real name beats rendering a blank row.
 *
 * @param roster Any roster-ish record. Null/undefined yields `fallback`.
 * @param fallback What to return when nothing is resolvable. Defaults to
 *   `'Unknown'`, matching the majority of existing call sites; the Setup
 *   panels pass their own wording.
 */
export function getRosterDisplayName(
  roster: RosterNameFields | null | undefined,
  fallback = 'Unknown',
): string {
  if (!roster) return fallback;
  const nickname = typeof roster.nickname === 'string' ? roster.nickname.trim() : '';
  if (nickname) return nickname;
  const name = typeof roster.name === 'string' ? roster.name.trim() : '';
  if (name) return name;
  const rosterName = typeof roster.roster_name === 'string' ? roster.roster_name.trim() : '';
  return rosterName || fallback;
}

/**
 * Searchable text for a roster: its nickname AND its provider name, lowercased.
 *
 * Mirrors `getStudentSearchText` (ENG-9258). Resolving the DISPLAY name alone
 * would fix the reported bug and quietly create its mirror image: once a roster
 * shows its nickname, a teacher who still knows the Clever name could no longer
 * find it. The rename is additive to what a roster is called, not a replacement
 * — the ticket asks for the old name to keep working as a fallback.
 *
 * @returns Lowercased, space-joined name fields. Test with
 *   `getRosterSearchText(r).includes(query.toLowerCase())`.
 */
export function getRosterSearchText(roster: RosterNameFields | null | undefined): string {
  if (!roster) return '';
  return [roster.nickname, roster.name, roster.roster_name]
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .join(' ')
    .toLowerCase();
}
